import React from 'react'
import styled, { keyframes } from 'styled-components'

export interface LoaderProps {
  size?: number
  color?: string
  className?: string
}

export const Loader: React.FC<LoaderProps> = ({
  size = 50,
  color,
  className,
}) => (
  <Root
    className={className}
    $size={size}
    style={color ? ({ '--ll-fg': color } as React.CSSProperties) : undefined}
    role='status'
    aria-label='loading'
  />
)

export default Loader

const rotate = keyframes`
  0%   { transform: rotate(0deg)   scale(0.8); }
  50%  { transform: rotate(360deg) scale(1.2); }
  100% { transform: rotate(720deg) scale(0.8); }
`

const ball1 = keyframes`
  0% {
    box-shadow:
      0 0 0 var(--ll-ring) var(--ll-fg),
      var(--ll-shadow) 0 0 0 var(--ll-fg);
  }
  50% {
    box-shadow:
      0 0 0 var(--ll-ring) var(--ll-fg),
      0 0 0 0 var(--ll-fg);
    margin-bottom: 0;
    transform: translate(var(--ll-travel), var(--ll-travel));
  }
  100% {
    box-shadow:
      0 0 0 var(--ll-ring) var(--ll-fg),
      var(--ll-shadow) 0 0 0 var(--ll-fg);
    margin-bottom: var(--ll-margin);
  }
`

const ball2 = keyframes`
  0% {
    box-shadow:
      var(--ll-shadow) 0 0 0 var(--ll-bg),
      var(--ll-shadow) 0 0 var(--ll-ring) var(--ll-fg);
  }
  50% {
    box-shadow:
      0 0 0 0 var(--ll-bg),
      0 0 0 var(--ll-ring) var(--ll-fg);
    margin-top: calc(-1 * var(--ll-ball));
    transform: translate(var(--ll-travel), var(--ll-travel));
  }
  100% {
    box-shadow:
      var(--ll-shadow) 0 0 0 var(--ll-bg),
      var(--ll-shadow) 0 0 var(--ll-ring) var(--ll-fg);
    margin-top: 0;
  }
`

const Root = styled.span<{ $size: number }>`
  --ll-size: ${({ $size }) => $size}px;
  --ll-ball: ${({ $size }) => $size * 0.4}px;
  --ll-shadow: ${({ $size }) => $size * 0.6}px;
  --ll-margin: ${({ $size }) => $size * 0.2}px;
  --ll-travel: ${({ $size }) => $size * 0.3}px;
  --ll-ring: 2px;
  --ll-fg: ${({ theme }) => theme.colors.accent};
  --ll-bg: #ffffff;

  display: inline-block;
  width: var(--ll-size);
  height: var(--ll-size);
  animation: ${rotate} 1.4s infinite;

  &::before,
  &::after {
    content: '';
    display: block;
    width: var(--ll-ball);
    height: var(--ll-ball);
    border-radius: 50%;
  }

  &::before {
    background: var(--ll-bg);
    box-shadow:
      0 0 0 var(--ll-ring) var(--ll-fg),
      var(--ll-shadow) 0 0 0 var(--ll-fg);
    margin-bottom: var(--ll-margin);
    animation: ${ball1} 1.4s infinite;
  }

  &::after {
    background: var(--ll-fg);
    box-shadow:
      var(--ll-shadow) 0 0 0 var(--ll-bg),
      var(--ll-shadow) 0 0 var(--ll-ring) var(--ll-fg);
    animation: ${ball2} 1.4s infinite;
  }
`
